import { useEffect, useMemo, useRef, useState } from 'react';

import { Typography as Text } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import classNames from 'classnames';

import {
  ToolExtensionOptions,
  ToolGroupEntry,
} from '../../extensions/tool-extension';
import { useToolExtensions } from '../../extensions/tool-extension';
import { FADE_IN_ANIMATION, fadeInKeyframes } from '../ThoughtProcess/styles';
import { collapseConsecutiveTools } from './collapseConsecutiveTools';
import { formatDefaultToolLabel, inferToolIcon } from './defaultToolMappings';
import { useSharedToolStyles } from './sharedStyles';

const FADE_MS = 200;
const HOLD_MS = 500;

type Phase = 'hold' | 'fading-out' | 'fading-in';

const useStyles = makeStyles({
  ...fadeInKeyframes,
  '@keyframes badgeFadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  badge: {
    alignItems: 'center',
    animation: '$badgeFadeIn 200ms ease-out',
    background: 'var(--background-elevated-highlight)',
    borderRadius: '50%',
    display: 'inline-flex',
    flexShrink: 0,
    height: '18px',
    justifyContent: 'center',
    width: '18px',
  },
  countNumber: {
    color: 'var(--text-subdued)',
    fontSize: 'var(--encore-text-size-smaller-3)',
    lineHeight: 1,
    transition: `opacity ${FADE_MS}ms ease-in-out`,
  },
  indented: {
    paddingLeft: 'calc(14px + var(--encore-spacing-tighter-2))',
  },
  item: {
    alignItems: 'center',
    animation: FADE_IN_ANIMATION,
    color: 'var(--text-subdued)',
    display: 'flex',
    gap: 'var(--encore-spacing-tighter-2)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--encore-spacing-tighter-2)',
  },
});

const InlineToolEntry = ({
  entry,
  ext,
}: {
  entry: ToolGroupEntry;
  ext: ToolExtensionOptions | undefined;
}) => {
  const shared = useSharedToolStyles();
  const IconComponent = ext?.icon ?? inferToolIcon(entry.tool);

  if (ext?.renderLabel) {
    return (
      <>
        <IconComponent size={14} className={shared.icon} />
        {ext.renderLabel(entry)}
      </>
    );
  }

  const label = ext?.formatLabel
    ? ext.formatLabel(entry)
    : formatDefaultToolLabel(entry);

  return (
    <>
      <IconComponent size={14} className={shared.icon} />
      <Text variant="body2" color="textSecondary">
        {label.text}
      </Text>
    </>
  );
};

const AnimatedCountBadge = ({ count }: { count: number }) => {
  const classes = useStyles();
  const [shown, setShown] = useState(count);
  const [phase, setPhase] = useState<Phase>('hold');
  const countRef = useRef(count);
  const rafRef = useRef<number>();
  countRef.current = count;

  useEffect(() => {
    if (phase === 'hold') {
      if (shown >= count) return undefined;
      const timer = setTimeout(() => setPhase('fading-out'), HOLD_MS);
      return () => clearTimeout(timer);
    }

    if (phase === 'fading-out') {
      const timer = setTimeout(() => {
        setShown(prev => Math.min(prev + 1, countRef.current));
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = requestAnimationFrame(() => {
            setPhase('fading-in');
          });
        });
      }, FADE_MS);
      return () => {
        clearTimeout(timer);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
    }

    if (phase === 'fading-in') {
      const timer = setTimeout(() => setPhase('hold'), FADE_MS);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [phase, shown, count]);

  if (count <= 1) return null;

  return (
    <span className={classes.badge}>
      <span
        className={classes.countNumber}
        style={{ opacity: phase === 'fading-out' ? 0 : 1 }}
      >
        {shown}
      </span>
    </span>
  );
};

export const InlineToolGroup = ({
  entries,
  indented = false,
}: {
  entries: ToolGroupEntry[];
  indented?: boolean;
}) => {
  const classes = useStyles();
  const toolExtensions = useToolExtensions();

  const collapsedEntries = useMemo(
    () => collapseConsecutiveTools(entries),
    [entries],
  );

  if (collapsedEntries.length === 1 && collapsedEntries[0].count === 1) {
    const ext = toolExtensions.find(e =>
      e.predicate(collapsedEntries[0].entry.tool),
    );

    return (
      <div
        className={classNames(classes.item, {
          [classes.indented]: indented,
        })}
      >
        <InlineToolEntry entry={collapsedEntries[0].entry} ext={ext} />
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.list, {
        [classes.indented]: indented,
      })}
    >
      {collapsedEntries.map((collapsed, i) => {
        const ext = toolExtensions.find(e => e.predicate(collapsed.entry.tool));

        return (
          <div key={i} className={classes.item}>
            <InlineToolEntry entry={collapsed.entry} ext={ext} />
            <AnimatedCountBadge count={collapsed.count} />
          </div>
        );
      })}
    </div>
  );
};
